import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { logger } from "shared/logger/logger";

/**
 * ErrorBoundary — implémentation correcte via React.Component + componentDidCatch.
 * Capture les erreurs de rendu React (contrairement à window.addEventListener).
 *
 * SECURITE : suppression du require('@sentry/react') dynamique.
 * Avant : componentDidCatch faisait un require() dynamique de Sentry, ce qui
 * est un anti-pattern dans un module ESM (mélange CJS/ESM), peut échouer
 * silencieusement, et contourne le tree-shaking de Vite.
 * Après : le logger centralisé (shared/logger/logger.js) encapsule déjà Sentry
 * de façon propre. logger.error() route vers Sentry en production et vers
 * console.error en développement. Un seul point d'entrée, pas de require().
 *
 * Usage :
 *   <ErrorBoundary>
 *     <MonComposant />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={<MaPageErreur />}>
 *     <MonComposant />
 *   </ErrorBoundary>
 */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // logger.error encapsule Sentry en prod et console.error en dev.
    // Pas de require() dynamique — cohérent avec le reste du projet ESM.
    logger.error(error, { feature: "ErrorBoundary", componentStack: errorInfo?.componentStack });
  }

  handleReload() {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 4,
          }}
          role="alert"
        >
          <Typography variant="h5" gutterBottom color="error">
            Une erreur est survenue
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            L'application a rencontré un problème inattendu.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Recharger la page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
